import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import "@/i18n";
import "@/App.css";
import { useStore } from "@/store/useStore";
import TelegramProvider from "@/components/TelegramProvider";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import LobbyPage from "@/pages/LobbyPage";
import GamePage from "@/pages/GamePage";
import TournamentsPage from "@/pages/TournamentsPage";
import WalletPage from "@/pages/WalletPage";
import ProfilePage from "@/pages/ProfilePage";
import LeaderboardPage from "@/pages/LeaderboardPage";
import PlayBotPage from "@/pages/PlayBotPage";
import BotGamePage from "@/pages/BotGamePage";
import AchievementsPage from "@/pages/AchievementsPage";
import FriendsPage from "@/pages/FriendsPage";
import SettingsPage from "@/pages/SettingsPage";
import AdminPage from "@/pages/AdminPage";
import MatchmakingPage from "@/pages/MatchmakingPage";
import LiveGamePage from "@/pages/LiveGamePage";
import TournamentDetailPage from "@/pages/TournamentDetailPage";
import ReferralsPage from "@/pages/ReferralsPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import BlogEditorPage from "@/pages/BlogEditorPage";
import GameHistoryPage from "@/pages/GameHistoryPage";
import TermsPage from "@/pages/TermsPage";

// Layout
import Layout from "@/components/Layout";

// Check if running in Telegram
import { isTelegramWebApp } from "@/lib/telegram";

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, token } = useStore();
  
  // In Telegram, allow access while auth is in progress
  if (isTelegramWebApp()) {
    return children;
  }
  
  if (!token && !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function App() {
  const { fetchUser, token, isAuthenticated } = useStore();

  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) localStorage.setItem("ref_code", ref.toUpperCase());
  }, []);

  useEffect(() => {
    // If we have a token, fetch user data
    if (token && !isAuthenticated) {
      fetchUser();
    }
  }, [token, isAuthenticated, fetchUser]);

  return (
    <TelegramProvider>
      <div className="min-h-screen bg-background">
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          
          {/* Protected routes with Layout */}
          <Route element={<Layout />}>
            <Route path="/lobby" element={
              <ProtectedRoute><LobbyPage /></ProtectedRoute>
            } />
            <Route path="/play-bot" element={
              <ProtectedRoute><PlayBotPage /></ProtectedRoute>
            } />
            <Route path="/bot-game/:gameId" element={
              <ProtectedRoute><BotGamePage /></ProtectedRoute>
            } />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/new" element={<ProtectedRoute><BlogEditorPage /></ProtectedRoute>} />
            <Route path="/blog/edit/:postId" element={<ProtectedRoute><BlogEditorPage /></ProtectedRoute>} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/tournaments" element={
              <ProtectedRoute><TournamentsPage /></ProtectedRoute>
            } />
            <Route path="/tournaments/:tournamentId" element={
              <ProtectedRoute><TournamentDetailPage /></ProtectedRoute>
            } />
            <Route path="/wallet" element={
              <ProtectedRoute><WalletPage /></ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute><ProfilePage /></ProtectedRoute>
            } />
            <Route path="/achievements" element={
              <ProtectedRoute><AchievementsPage /></ProtectedRoute>
            } />
            <Route path="/friends" element={
              <ProtectedRoute><FriendsPage /></ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute><SettingsPage /></ProtectedRoute>
            } />
            <Route path="/admin" element={
              <ProtectedRoute><AdminPage /></ProtectedRoute>
            } />
            <Route path="/game/:gameId" element={
              <ProtectedRoute><LiveGamePage /></ProtectedRoute>
            } />
            <Route path="/matchmaking" element={
              <ProtectedRoute><MatchmakingPage /></ProtectedRoute>
            } />
            <Route path="/game-history" element={
              <ProtectedRoute><GameHistoryPage /></ProtectedRoute>
            } />
            <Route path="/leaderboard" element={
              <ProtectedRoute><LeaderboardPage /></ProtectedRoute>
            } />
            <Route path="/referrals" element={
              <ProtectedRoute><ReferralsPage /></ProtectedRoute>
            } />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
    </TelegramProvider>
  );
}

export default App;
